// Centralized API Service

const API_BASE_URL = 'https://leadapi.crmbytes.com/api';
const GEO_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEO_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    const data = await response.json();
    
    if (data.isSuccess && data.statusCode === 200) {
      return {
        success: true,
        data: data.data
      };
    }
    
    return {
      success: false,
      data: null
    };
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      data: null
    };
  }
};

/**
 * Extract numeric phone number (remove all non-numeric characters)
 */
const extractNumericPhone = (phoneNumber) => {
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Get country code (2-letter) from dial code
 */
const getCountryCodeFromDialCode = (dialCode, countryCodes) => {
  const country = countryCodes.find(c => c.dial === dialCode);
  return country ? country.code : 'US';
};

/**
 * Submit lead to API
 * @param {Object} formData - Form data containing name, email, phone, countryCode, message
 * @param {Object} geoData - Geolocation data from fetchGeolocation (optional)
 * @param {Array} countryCodes - Array of country codes for mapping (optional)
 * @param {string} packageTitle - Package title (optional)
 * @param {number} packageAmount - Package amount (optional)
 * @param {number} tmSignUpType - Sign up type (1 = Self Filing, 2 = Attorney-Led) (optional)
 */
export const submitLead = async (formData, geoData = null, countryCodes = [], packageTitle = '', packageAmount = 0, tmSignUpType = 1) => {
  try {
    // Extract numeric phone number only (no country code prefix)
    const numericPhone = extractNumericPhone(formData.phone);
    
    // Get country code (2-letter) from dial code
    const countryCode = getCountryCodeFromDialCode(formData.countryCode, countryCodes);
    
    // Prepare payload according to API specification
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryCode.replace('+', '')),
      country: countryCode,
      phoneNumber: numericPhone,
      ipAddress: geoData?.ipAddress || '',
      description: formData.message || '',
      pageUrl: window.location.href,
      city: geoData?.cityName || '',
      state: geoData?.regionName || '',
      geoLocation: geoData ? JSON.stringify({
        isSuccess: true,
        data: geoData,
        errorMessage: '',
        statusCode: 200
      }) : '',
      packageTitle: packageTitle || '',
      packageAmount: parseFloat(String(packageAmount).replace(/[^0-9.]/g, '')) || 0,
      tmSignUpType: tmSignUpType || 1
    };

    console.log('Submitting lead payload:', payload);

    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('Lead API response:', data);

    // Handle status code 200 - Success
    if (response.status === 200 && data.isSuccess && data.data?.customerId) {
      return {
        success: true,
        customerId: data.data.customerId,
        message: data.data.message || 'Lead created successfully',
        redirect: `/thanks?customerid=${data.data.customerId}`
      };
    }

    // Handle status code 401 - Unauthorized
    if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access',
        statusCode: 401
      };
    }

    // Handle status code 400 - Validation Error
    if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data provided',
        statusCode: 400
      };
    }

    // Handle other errors
    return {
      success: false,
      error: data.errorMessage || data.message || 'Something went wrong. Please try again.',
      statusCode: response.status
    };

  } catch (error) {
    console.error('Lead API Error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.',
      statusCode: 500
    };
  }
};

/**
 * Get country code from geolocation data
 */
export const getCountryFromGeo = (geoData) => {
  if (geoData && geoData.countryCode) {
    return geoData.countryCode;
  }
  return 'US'; // Default to US
};

/**
 * Find country dial code from country code (2-letter)
 */
export const getDialCodeFromCountryCode = (countryCode, countryCodes) => {
  const country = countryCodes.find(c => c.code === countryCode);
  return country ? country.dial : '+1';
};
